import {
  confirmUserAttribute,
  fetchUserAttributes,
  getCurrentUser,
  signOut,
  updateUserAttribute,
  deleteUser,
  fetchAuthSession,
} from "@aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
import { useCallback, useEffect, useState } from "react";

import {
  createOrganizationCPs,
  createGeneralInformation,
  updateGeneralInformation,
  updateOrganizationCPs,
  updateUserLink,
} from "../graphql/mutations.js";
import {
  listGeneralInformations,
  listOrganizationCPs,
  listOrganizationInformations,
  listUserLinks,
} from "../graphql/queries.js";

const client = generateClient();

const normalizeEmail = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();
const normalizeIdentity = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();
// The default @auth owner field is stored as compound "sub::sub" (or
// "sub::username"), not a bare identity value - match against any segment.
const ownerMatchesAliases = (owner, aliasSet) => {
  const normalized = normalizeIdentity(owner);
  if (!normalized) return false;
  if (aliasSet.has(normalized)) return true;
  return normalized.split("::").some((part) => aliasSet.has(part));
};

export default function GeneralInformation() {
  const [formData, setFormData] = useState({
    FirstName: "",
    LastName: "",
    Email: "",
    ProfileType: "",
    ProfessionalRole: "",
    BusinessName: "",
    BusinessEIN: "",
    BusinessAddress: "",
    BusinessCountry: "",
    BusinessCity: "",
    BusinessState: "",
    BusinessZipCode: "",
    BusinessPhoneNumber: "",
    BusinessWebsite: "",
    OrganizationAdministrator: false,
    OrganizationEmployee: false,

    // NEW: Added new fields
    Department: "",
    ProgramsUnit: "",
    Service: "",
  });
  const states = [
    "Alabama",
    "Alaska",
    "Arizona",
    "Arkansas",
    "California",
    "Colorado",
    "Connecticut",
    "Delaware",
    "District of Columbia",
    "Florida",
    "Georgia",
    "Hawaii",
    "Idaho",
    "Illinois",
    "Indiana",
    "Iowa",
    "Kansas",
    "Kentucky",
    "Louisiana",
    "Maine",
    "Maryland",
    "Massachusetts",
    "Michigan",
    "Minnesota",
    "Mississippi",
    "Missouri",
    "Montana",
    "Nebraska",
    "Nevada",
    "New Hampshire",
    "New Jersey",
    "New Mexico",
    "New York",
    "North Carolina",
    "North Dakota",
    "Ohio",
    "Oklahoma",
    "Oregon",
    "Pennsylvania",
    "Rhode Island",
    "South Carolina",
    "South Dakota",
    "Tennessee",
    "Texas",
    "Utah",
    "Vermont",
    "Virginia",
    "Washington",
    "West Virginia",
    "Wisconsin",
    "Wyoming",
  ];
  const [userId, setUserId] = useState("");
  const [cognitoEmail, setCognitoEmail] = useState("");
  const [cognitoAccountType, setCognitoAccountType] = useState("");
  const [cognitoOrganizationId, setCognitoOrganizationId] = useState("");
  const [recordId, setRecordId] = useState(null);
  const [awaitingVerification, setAwaitingVerification] = useState(false);
  const [confirmCode, setConfirmCode] = useState("");
  const [notification, setNotification] = useState("");
  const [statusMessage, setStatusMessage] = useState(
    "Loading user information...",
  );
  const [BusinessVerified, setBusinessVerified] = useState(false);
  const [BusinessStatus, setBusinessStatus] = useState("");
  const [orgOptions, setOrgOptions] = useState({
    Departments: [],
    ProgramsUnits: [],
    Services: [],
  });
  const [orgEmployeeError, setOrgEmployeeError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  async function syncUserLinkEmails(uid, newEmailRaw) {
    const newEmail = String(newEmailRaw || "")
      .trim()
      .toLowerCase();
    if (!uid || !newEmail) return;

    try {
      let nextToken = null;
      const links = [];
      do {
        const res = await client.graphql({
          query: listUserLinks,
          variables: {
            filter: {
              or: [{ clientId: { eq: uid } }, { professionalId: { eq: uid } }],
            },
            limit: 200,
            nextToken,
          },
        });
        const batch = res?.data?.listUserLinks?.items || [];
        links.push(...batch);
        nextToken = res?.data?.listUserLinks?.nextToken || null;
      } while (nextToken);

      const updates = [];
      for (const link of links) {
        if (link?.clientId === uid && link?.clientEmail !== newEmail) {
          updates.push({ id: link.id, clientEmail: newEmail });
        }
        if (
          link?.professionalId === uid &&
          link?.professionalEmail !== newEmail
        ) {
          updates.push({ id: link.id, professionalEmail: newEmail });
        }
      }

      for (const input of updates) {
        await client.graphql({
          query: updateUserLink,
          variables: { input },
        });
      }
    } catch (err) {
      console.warn("Failed to sync UserLink emails:", err);
    }
  }

  const resolveOrganizationIdForUser = useCallback(
    async ({ uid, profileType, cognitoOrgId, emailCandidates = [] }) => {
      if (cognitoOrgId) return cognitoOrgId;
      if (profileType === "Organization" && uid) return uid;

      const normalizedCandidates = Array.from(
        new Set(emailCandidates.map((email) => normalizeEmail(email))),
      ).filter(Boolean);
      if (!normalizedCandidates.length) return "";

      const emailFilter =
        normalizedCandidates.length === 1
          ? { OrganizationCPEmail: { eq: normalizedCandidates[0] } }
          : {
              or: normalizedCandidates.map((email) => ({
                OrganizationCPEmail: { eq: email },
              })),
            };

      try {
        const cpRes = await client.graphql({
          query: listOrganizationCPs,
          variables: { filter: emailFilter, limit: 100 },
        });

        const cpItems = cpRes?.data?.listOrganizationCPs?.items || [];
        const matched = cpItems.find(
          (item) =>
            normalizedCandidates.includes(
              normalizeEmail(item.OrganizationCPEmail),
            ) && item.OrganizationId,
        );
        return matched?.OrganizationId || "";
      } catch (err) {
        console.warn(
          "Unable to resolve organizationId from OrganizationCPs:",
          err,
        );
        return "";
      }
    },
    [],
  );

  useEffect(() => {
    async function loadData() {
      try {
        // ensure we have the current user's email and attributes

        const user = await getCurrentUser();
        const uid = user?.userId || "";
        const rawEmail = user?.signInDetails?.loginId || "";
        let email = rawEmail.trim().toLowerCase();

        setUserId(uid);
        setCognitoEmail(email);

        // Keep any existing connections up-to-date with the user's current email.
        // This fixes stale client filters (e.g., SentRecommendations) after an email change.
        syncUserLinkEmails(uid, email);

        // Fetch attributes from Cognito
        const attrs = await fetchUserAttributes();
        const firstName = attrs["given_name"] || "";
        const lastName = attrs["family_name"] || "";
        const profileType = attrs["custom:accountType"] || "";
        const cognitoOrganizationIdRaw =
          attrs["custom:organizationId"] || attrs["custom:organizationID"];
        if (!email) {
          email = normalizeEmail(attrs.email);
        }
        setCognitoEmail(email);
        const effectiveOrganizationId = await resolveOrganizationIdForUser({
          uid,
          profileType,
          cognitoOrgId: cognitoOrganizationIdRaw,
          emailCandidates: [email],
        });
        setCognitoOrganizationId(effectiveOrganizationId || "");
        if (profileType === "Professional") {
          try {
            const response = await fetch(
              "https://kdbbptgaw3jz222sq46ywyfs4u0tjayt.lambda-url.us-east-1.on.aws/",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  action: "addUserToGroups",
                  email,
                  groups: ["Professional"],
                }),
              },
            );
            if (response.ok) {
              console.log(
                "User added to Professional group. Refreshing session claims...",
              );
              await fetchAuthSession({ forceRefresh: true });
              console.log(
                "Session claims updated successfully. No sign-outs will happen.",
              );
            }
          } catch (err) {
            console.error("Failed to add user to Professional group:", err);
          }
        }
        setCognitoAccountType(profileType);

        // Check for existing record in DynamoDB by userId OR Email
        const ownerAliases = new Set(
          [uid, user?.username, user?.signInDetails?.loginId, attrs?.sub, email]
            .map((value) => normalizeIdentity(value))
            .filter(Boolean),
        );

        const res = await client.graphql({
          query: /* GraphQL */ `
            query ListGeneralInformations(
              $filter: ModelGeneralInformationFilterInput
              $limit: Int
            ) {
              listGeneralInformations(filter: $filter, limit: $limit) {
                items {
                  id
                  userId
                  owner
                  organizationId
                  FirstName
                  LastName
                  Email
                  ProfileType
                  ProfessionalRole
                  BusinessName
                  BusinessEIN
                  BusinessAddress
                  BusinessCountry
                  BusinessCity
                  BusinessState
                  BusinessZipCode
                  BusinessPhoneNumber
                  BusinessWebsite
                  OrganizationAdministrator
                  OrganizationEmployee
                  Department
                  ProgramsUnit
                  Service
                }
              }
            }
          `,
          variables: {
            filter: {
              or: [{ userId: { eq: uid } }, { Email: { eq: email } }],
            },
            limit: 5,
          },
        });

        const items = res.data?.listGeneralInformations?.items || [];
        const ownedRecord =
          items.find((it) => ownerMatchesAliases(it?.owner, ownerAliases)) ||
          null;
        // Prefer the most complete email match (has actual profile data) over
        // a blank duplicate created by a race/retry.
        const emailMatches = items.filter(
          (it) => normalizeEmail(it?.Email) === email,
        );
        const emailMatchedRecord =
          emailMatches.find((it) => it?.FirstName || it?.BusinessName) ||
          emailMatches[0] ||
          null;
        let existing = ownedRecord;
        let ownershipNeedsRepair = false;

        if (!ownedRecord && emailMatchedRecord) {
          // Most likely an account recreation (new Cognito sub, e.g. after a
          // lost MFA/TOTP reset): reuse the existing profile instead of
          // creating a blank duplicate, and try to self-heal it. If the
          // update is rejected (owner field truly doesn't match), an admin
          // needs to run scripts/heal-orphaned-accounts.cjs to relink it.
          console.warn(
            "Found GeneralInformation by email but not owned by current user; attempting to reattach it instead of creating a duplicate.",
            {
              uid,
              email,
              emailMatchedId: emailMatchedRecord.id,
            },
          );
          existing = emailMatchedRecord;
          try {
            await client.graphql({
              query: updateGeneralInformation,
              variables: { input: { id: emailMatchedRecord.id, userId: uid } },
            });
          } catch (reattachError) {
            ownershipNeedsRepair = true;
            console.warn(
              "Could not reattach existing profile to current session; needs admin repair:",
              reattachError,
            );
          }
        }

        // Backfill legacy profiles created before organizationId existed in schema.
        if (effectiveOrganizationId && items.length > 0) {
          const recordsNeedingOrgId = items.filter(
            (it) =>
              !it?.organizationId &&
              ownerMatchesAliases(it?.owner, ownerAliases),
          );

          for (const record of recordsNeedingOrgId) {
            try {
              await client.graphql({
                query: updateGeneralInformation,
                variables: {
                  input: {
                    id: record.id,
                    organizationId: effectiveOrganizationId,
                  },
                },
              });
            } catch (orgBackfillError) {
              console.warn(
                "Failed to backfill organizationId on legacy profile record:",
                record?.id,
                orgBackfillError,
              );
            }
          }
        }

        if (items.length > 1) {
          console.warn(
            "Multiple GeneralInformation records found for user; using owned match:",
            {
              uid,
              email,
              count: items.length,
            },
          );
        }

        if (existing) {
          // ✅ Existing record found
          setRecordId(existing.id);
          setFormData({
            FirstName: existing.FirstName || firstName,
            LastName: existing.LastName || lastName,
            Email: existing.Email || email,
            ProfileType: existing.ProfileType || profileType,
            ProfessionalRole: existing.ProfessionalRole || "",
            BusinessName: existing.BusinessName || "",
            BusinessEIN: existing.BusinessEIN || "",
            BusinessAddress: existing.BusinessAddress || "",
            BusinessCountry: existing.BusinessCountry || "",
            BusinessCity: existing.BusinessCity || "",
            BusinessState: existing.BusinessState || "",
            BusinessZipCode: existing.BusinessZipCode || "",
            BusinessPhoneNumber: existing.BusinessPhoneNumber || "",
            BusinessWebsite: existing.BusinessWebsite || "",
            OrganizationAdministrator:
              existing.OrganizationAdministrator || false,
            OrganizationEmployee: existing.OrganizationEmployee || false,
            Department: existing.Department || "",
            ProgramsUnit: existing.ProgramsUnit || "",
            Service: existing.Service || "",
          });

          if (existing.OrganizationEmployee && existing.BusinessName?.trim()) {
            verifyBusiness(existing.BusinessName.trim())
              .then((ok) => setBusinessVerified(ok))
              .catch(console.error);
          }

          // OrganizationCPs (managed by the org admin on the Employees page)
          // is the source of truth for admin status - reflect it here rather
          // than trusting this record's own (independently-editable) copy.
          if (existing.OrganizationEmployee) {
            try {
              const cpRes = await client.graphql({
                query: listOrganizationCPs,
                variables: {
                  filter: { OrganizationCPEmail: { eq: email } },
                  limit: 1,
                },
              });
              const cp = cpRes?.data?.listOrganizationCPs?.items?.[0];
              if (cp) {
                setFormData((prev) => ({
                  ...prev,
                  OrganizationAdministrator: !!cp.OrganizationAdministrator,
                }));
              }
            } catch (cpSyncError) {
              console.warn(
                "Could not sync administrator status from OrganizationCPs:",
                cpSyncError,
              );
            }
          }

          if (
            effectiveOrganizationId &&
            existing.id &&
            existing.organizationId !== effectiveOrganizationId
          ) {
            try {
              await client.graphql({
                query: updateGeneralInformation,
                variables: {
                  input: {
                    id: existing.id,
                    organizationId: effectiveOrganizationId,
                  },
                },
              });
            } catch (e) {
              console.warn(
                "Failed to persist resolved organizationId on profile:",
                e,
              );
            }
          }

          setStatusMessage(
            ownershipNeedsRepair
              ? "Loaded your information. Some saves may be blocked until an administrator relinks your account; contact support if you see a permissions error."
              : "Loaded your information.",
          );
        } else {
          // 🚀 No record found — create one using Cognito attributes
          const newUserData = {
            userId: uid,
            organizationId: effectiveOrganizationId || null,
            FirstName: firstName,
            LastName: lastName,
            Email: email, // already lowercased
            ProfileType: profileType,
          };

          // Double-check for any record by userId or email before creating
          const recheckRes = await client.graphql({
            query: /* GraphQL */ `
              query ListGeneralInformations(
                $filter: ModelGeneralInformationFilterInput
                $limit: Int
              ) {
                listGeneralInformations(filter: $filter, limit: $limit) {
                  items {
                    id
                    userId
                    organizationId
                    Email
                    FirstName
                    LastName
                    ProfileType
                  }
                }
              }
            `,
            variables: {
              filter: {
                or: [{ userId: { eq: uid } }, { Email: { eq: email } }],
              },
              limit: 5,
            },
          });

          const existingNowItems =
            recheckRes.data?.listGeneralInformations?.items || [];
          const existingNow =
            existingNowItems.find((it) => it.userId === uid) || null;

          if (existingNow) {
            // Use the existing record rather than creating a duplicate
            setRecordId(existingNow.id);
            setFormData((prev) => ({
              ...prev,
              FirstName: existingNow.FirstName || firstName,
              LastName: existingNow.LastName || lastName,
              Email: existingNow.Email || email,
              ProfileType: existingNow.ProfileType || profileType,
            }));

            if (
              effectiveOrganizationId &&
              existingNow.id &&
              existingNow.organizationId !== effectiveOrganizationId
            ) {
              try {
                await client.graphql({
                  query: updateGeneralInformation,
                  variables: {
                    input: {
                      id: existingNow.id,
                      organizationId: effectiveOrganizationId,
                    },
                  },
                });
              } catch (e) {
                console.warn("Failed to persist organizationId on profile:", e);
              }
            }
            setStatusMessage("Loaded your information.");
          } else {
            const createRes = await client.graphql({
              query: createGeneralInformation,
              variables: { input: newUserData },
            });

            const newRecordId = createRes.data?.createGeneralInformation?.id;
            if (newRecordId) setRecordId(newRecordId);

            setFormData((prev) => ({ ...prev, ...newUserData }));

            console.info(
              "Created GeneralInformation record:",
              newUserData.Email,
            );
            setStatusMessage("Created new record from your Cognito profile.");
          }
        }
      } catch (err) {
        console.error("Error loading data:", err);
        setStatusMessage("Error loading information.");
      }
    }

    loadData();
  }, [resolveOrganizationIdForUser]);

  async function updateOrgCPIfEmployee() {
    try {
      const rawCandidates = Array.from(
        new Set([
          String(formData.Email || "").trim(),
          String(cognitoEmail || "").trim(),
        ]),
      ).filter(Boolean);

      const normalizedCandidateEmails = Array.from(
        new Set(rawCandidates.map((email) => normalizeEmail(email))),
      ).filter(Boolean);

      if (!normalizedCandidateEmails.length) {
        return { updatedCount: 0, reason: "no-email-candidates" };
      }

      const effectiveOrgId =
        cognitoOrganizationId ||
        (await resolveOrganizationIdForUser({
          uid: userId,
          profileType: formData.ProfileType,
          cognitoOrgId: cognitoOrganizationId,
          emailCandidates: normalizedCandidateEmails,
        }));

      const emailFilterTerms = Array.from(
        new Set([
          ...rawCandidates,
          ...normalizedCandidateEmails,
          ...rawCandidates.map((email) => email.toLowerCase()),
        ]),
      ).map((email) => ({ OrganizationCPEmail: { eq: email } }));

      const emailFilter =
        emailFilterTerms.length === 1
          ? emailFilterTerms[0]
          : { or: emailFilterTerms };

      const res = await client.graphql({
        query: listOrganizationCPs,
        variables: {
          filter: emailFilter,
        },
      });
      // 1️⃣ Get all OrganizationCP records matching this email

      let cpRecords = (res.data?.listOrganizationCPs?.items || []).filter(
        (cp) =>
          normalizedCandidateEmails.includes(
            normalizeEmail(cp.OrganizationCPEmail),
          ),
      );

      let allOrgCPs = [];

      // Fallback: If no email match found, search by organizationId instead
      if (!cpRecords.length && effectiveOrgId) {
        console.warn(
          `No OrganizationCP found by email ${normalizedCandidateEmails.join(", ")}. ` +
            `Falling back to organization search for orgId: ${effectiveOrgId}`,
        );
        let nextToken = null;
        do {
          const orgRes = await client.graphql({
            query: listOrganizationCPs,
            variables: {
              filter: {
                OrganizationId: { eq: effectiveOrgId },
              },
              limit: 1000,
              nextToken,
            },
          });

          const pageItems = orgRes.data?.listOrganizationCPs?.items || [];
          allOrgCPs.push(...pageItems);
          nextToken = orgRes.data?.listOrganizationCPs?.nextToken || null;
        } while (nextToken);

        // Find CP records that might be this user by looking for email match (case-insensitive)
        cpRecords = allOrgCPs.filter((cp) =>
          normalizedCandidateEmails.includes(
            normalizeEmail(cp.OrganizationCPEmail),
          ),
        );

        // Secondary fallback: if email drifted, match by name within the same org.
        if (!cpRecords.length) {
          const normalizedFirst = String(formData.FirstName || "")
            .trim()
            .toLowerCase();
          const normalizedLast = String(formData.LastName || "")
            .trim()
            .toLowerCase();

          if (normalizedFirst && normalizedLast) {
            const nameMatches = allOrgCPs.filter((cp) => {
              const cpFirst = String(cp.OrganizationCPFirstName || "")
                .trim()
                .toLowerCase();
              const cpLast = String(cp.OrganizationCPLastName || "")
                .trim()
                .toLowerCase();
              return cpFirst === normalizedFirst && cpLast === normalizedLast;
            });

            if (nameMatches.length === 1) {
              cpRecords = [nameMatches[0]];
            }
          }
        }
      }

      if (!cpRecords.length) {
        // Last fallback: create CP record for this employee in their org.
        if (effectiveOrgId && formData.OrganizationEmployee) {
          try {
            const emailToPersist =
              normalizeEmail(formData.Email) || normalizeEmail(cognitoEmail);
            await client.graphql({
              query: createOrganizationCPs,
              variables: {
                input: {
                  OrganizationId: effectiveOrgId,
                  OrganizationCPEmail: emailToPersist,
                  OrganizationCPFirstName: formData.FirstName || null,
                  OrganizationCPLastName: formData.LastName || null,
                  OrganizationCPRole: formData.ProfessionalRole || null,
                  primaryDepartment: formData.Department || null,
                  primaryProgramsUnit: formData.ProgramsUnit || null,
                  primaryService: formData.Service || null,
                  OrganizationAdministrator:
                    !!formData.OrganizationAdministrator,
                  PrimaryOrganizationAdministrator: false,
                },
              },
            });

            console.log(
              "Created missing OrganizationCP record from employee GeneralInformation update.",
            );
            return { updatedCount: 1, reason: "created" };
          } catch (createErr) {
            const createMessage = String(
              createErr?.errors?.[0]?.message ||
                createErr?.message ||
                createErr,
            ).toLowerCase();
            if (createMessage.includes("not authorized")) {
              return { updatedCount: 0, reason: "auth-denied" };
            }
            console.error("Failed to create OrganizationCP record:", createErr);
            return { updatedCount: 0, reason: "unexpected-error" };
          }
        }

        console.log(
          `No OrganizationCP records found for emails: ${normalizedCandidateEmails.join(", ")}. ` +
            `User may not be a registered employee.`,
        );
        return { updatedCount: 0, reason: "no-cp-match" };
      }

      // 2️⃣ Update each matched OrganizationCP with current form data
      let updatedCount = 0;
      let authDeniedCount = 0;
      for (const cp of cpRecords) {
        const updateInput = {
          id: cp.id,
          OrganizationCPFirstName:
            formData.FirstName || cp.OrganizationCPFirstName,
          OrganizationCPLastName:
            formData.LastName || cp.OrganizationCPLastName,
          OrganizationCPRole: formData.ProfessionalRole || null,
          primaryDepartment: formData.Department || null,
          primaryProgramsUnit: formData.ProgramsUnit || null,
          primaryService: formData.Service || null,
        };

        // Only update email if provided and different
        if (
          formData.Email &&
          normalizeEmail(formData.Email) !==
            normalizeEmail(cp.OrganizationCPEmail)
        ) {
          updateInput.OrganizationCPEmail = normalizeEmail(formData.Email);
        }

        try {
          await client.graphql({
            query: updateOrganizationCPs,
            variables: { input: updateInput },
          });
          updatedCount += 1;
        } catch (updateErr) {
          const message = String(
            updateErr?.errors?.[0]?.message || updateErr?.message || updateErr,
          ).toLowerCase();
          if (message.includes("not authorized")) {
            authDeniedCount += 1;
          } else {
            console.error("Failed to update OrganizationCP row:", updateErr);
          }
        }
      }

      if (updatedCount > 0) {
        console.log(
          `OrganizationCP records updated successfully. Updated ${updatedCount} record(s).`,
        );
        return { updatedCount, reason: "updated" };
      }

      if (authDeniedCount > 0) {
        console.warn(
          "OrganizationCP update blocked by auth. Token email and/or custom:organizationId may not match CP authorization rules.",
        );
        return { updatedCount: 0, reason: "auth-denied" };
      }

      return { updatedCount: 0, reason: "no-successful-updates" };
    } catch (err) {
      console.error("Error updating OrganizationCP records:", err);
      return { updatedCount: 0, reason: "unexpected-error" };
    }
  }

  useEffect(() => {
    const name = formData.BusinessName ?? "";
    if (formData.OrganizationEmployee && name.trim()) {
      resolveOrganizationIdForUser({
        uid: userId,
        profileType: formData.ProfileType,
        cognitoOrgId: cognitoOrganizationId,
        emailCandidates: [formData.Email, cognitoEmail],
      })
        .then((orgId) => verifyBusiness(name.trim(), orgId))
        .then((ok) => setBusinessVerified(ok))
        .catch(console.error);
    } else {
      setBusinessVerified(false);
    }
  }, [
    formData.OrganizationEmployee,
    formData.BusinessName,
    formData.Email,
    formData.ProfileType,
    userId,
    cognitoEmail,
    cognitoOrganizationId,
    resolveOrganizationIdForUser,
  ]);

  const verifyBusiness = async (BusinessName, organizationId) => {
    try {
      // Prefer matching by the employee's resolved organizationId (reliable,
      // survives punctuation/case differences in the typed business name);
      // fall back to an exact BusinessName match if no orgId is available.
      const normalizedTypedName = BusinessName.trim().toLowerCase();
      const filter = organizationId
        ? {
            or: [
              { organizationId: { eq: organizationId } },
              { BusinessName: { eq: BusinessName } },
            ],
          }
        : { BusinessName: { eq: BusinessName } };

      const result = await client.graphql({
        query: /* GraphQL */ `
          query ListOrganizationInformations(
            $filter: ModelOrganizationInformationFilterInput
          ) {
            listOrganizationInformations(filter: $filter) {
              items {
                id
                organizationId
                BusinessName
                BusinessEIN
                BusinessCountry
                BusinessAddress
                BusinessCity
                BusinessState
                BusinessZipCode
                BusinessPhoneNumber
                BusinessWebsite
                Departments
                ProgramsUnits
                Services
              }
            }
          }
        `,
        variables: { filter },
      });

      const items = result?.data?.listOrganizationInformations?.items || [];
      const item =
        items.find((it) => it.organizationId === organizationId) ||
        items.find(
          (it) =>
            String(it.BusinessName || "")
              .trim()
              .toLowerCase() === normalizedTypedName,
        ) ||
        items[0];

      if (item) {
        // ✅ Auto-fill form data with organization details
        setFormData((prev) => ({
          ...prev,
          BusinessEIN: item.BusinessEIN?.toString() || "",
          BusinessCountry: item.BusinessCountry || "",
          BusinessAddress: item.BusinessAddress || "",
          BusinessCity: item.BusinessCity || "",
          BusinessState: item.BusinessState || "",
          BusinessZipCode: item.BusinessZipCode?.toString() || "",
          BusinessPhoneNumber: item.BusinessPhoneNumber?.toString() || "",
          BusinessWebsite: item.BusinessWebsite || "",
        }));

        // ✅ Update dropdown options
        setOrgOptions({
          Departments: item.Departments || [],
          ProgramsUnits: item.ProgramsUnits || [],
          Services: item.Services || [],
        });

        setBusinessStatus("Business found ✅");
        return true;
      } else {
        setBusinessStatus("Business not found ❌");
        return false;
      }
    } catch (error) {
      const message = String(
        error?.errors?.[0]?.message || error?.message || error,
      );
      if (message.toLowerCase().includes("not authorized")) {
        setBusinessStatus(
          "Organization lookup requires an invite from your Organization Admin.",
        );
        return false;
      }

      console.error("Error verifying Business:", error);
      setBusinessStatus("Error checking Business ❌");
      return false;
    }
  };

  const validateOrganizationEmployee = async () => {
    try {
      const emailCandidates = Array.from(
        new Set([normalizeEmail(cognitoEmail), normalizeEmail(formData.Email)]),
      ).filter(Boolean);
      if (!emailCandidates.length) {
        // Some Cognito flows don't expose signInDetails.loginId; trust org claim if present.
        return !!cognitoOrganizationId;
      }

      const emailFilter =
        emailCandidates.length === 1
          ? { OrganizationCPEmail: { eq: emailCandidates[0] } }
          : {
              or: emailCandidates.map((email) => ({
                OrganizationCPEmail: { eq: email },
              })),
            };

      // Step 1: Check employee registration by email in OrganizationCPs.
      const cpRes = await client.graphql({
        query: /* GraphQL */ `
          query ListOrganizationCPs($filter: ModelOrganizationCPsFilterInput) {
            listOrganizationCPs(filter: $filter) {
              items {
                id
                OrganizationCPEmail
                OrganizationId
              }
            }
          }
        `,
        variables: { filter: emailFilter },
      });

      const cpItems = cpRes.data?.listOrganizationCPs?.items || [];
      const matchingCpItems = cpItems.filter((cp) =>
        emailCandidates.includes(normalizeEmail(cp.OrganizationCPEmail)),
      );

      if (!matchingCpItems.length) {
        // No CP records found by email. Fall back to cognitoOrganizationId to avoid
        // false rejection due to schema auth limits or CP lookup unavailability.
        return !!cognitoOrganizationId;
      }

      // Step 2 (best-effort): if BusinessName is provided, try to confirm at least one
      // matched CP belongs to that org name. If org lookup is blocked by auth/schema,
      // keep email-based validation success instead of false-rejecting employees.
      const normalizedBusinessName = String(formData.BusinessName || "")
        .trim()
        .toLowerCase();
      if (!normalizedBusinessName) return true;

      try {
        const orgRes = await client.graphql({
          query: listOrganizationInformations,
          variables: { limit: 500 },
        });

        const orgItems = orgRes.data?.listOrganizationInformations?.items || [];
        const cpOrgIds = new Set(
          matchingCpItems.map((cp) => cp.OrganizationId).filter(Boolean),
        );

        const relatedOrgs = orgItems.filter((org) => {
          const orgKey = org.organizationId || org.userId || org.id;
          return cpOrgIds.has(orgKey);
        });

        const nameMatched = relatedOrgs.some(
          (org) =>
            String(org.BusinessName || "")
              .trim()
              .toLowerCase() === normalizedBusinessName,
        );

        if (!nameMatched && relatedOrgs.length > 0) {
          const canonicalBusinessName = relatedOrgs[0].BusinessName || "";
          if (
            canonicalBusinessName &&
            canonicalBusinessName !== formData.BusinessName
          ) {
            setFormData((prev) => ({
              ...prev,
              BusinessName: canonicalBusinessName,
            }));
            setBusinessStatus(
              "Organization name updated by your admin; synced to current name.",
            );
          }
        }

        // Employee registration is determined by OrganizationCP email membership.
        // BusinessName mismatch (e.g., org rename) should not block valid employees.
        return true;
      } catch (orgLookupError) {
        console.warn(
          "Organization lookup unavailable; using email-based employee validation:",
          orgLookupError,
        );
        return true;
      }
    } catch (error) {
      const message = String(
        error?.errors?.[0]?.message || error?.message || error,
      );
      if (message.toLowerCase().includes("not authorized")) {
        // If schema auth blocks CP/org lookups but Cognito has org claim, don't false-reject.
        return !!cognitoOrganizationId;
      }
      console.error("Error validating org employee:", error);
      // Last-resort fallback to Cognito org claim.
      return !!cognitoOrganizationId;
    }
  };

  const showNotification = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(""), 4000);
  };

  const getFriendlyGraphQLError = (err) => {
    const graphQLErrorMessage =
      err?.errors?.[0]?.message ||
      err?.data?.errors?.[0]?.message ||
      err?.originalError?.errors?.[0]?.message ||
      err?.cause?.errors?.[0]?.message;

    const baseMessage =
      graphQLErrorMessage || err?.message || "Unknown save error.";

    if (String(baseMessage).toLowerCase().includes("not authorized")) {
      return (
        "Save blocked by permissions. Your profile record owner/organization claim does not match this session. " +
        "Please sign out/in and ask your org admin to re-invite your account if needed."
      );
    }

    return String(baseMessage);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  async function saveDynamoFields(includeEmail = false) {
    if (formData.OrganizationEmployee) {
      if (!formData.BusinessName?.trim()) {
        showNotification(
          "Please enter a valid Business Name before proceeding.",
        );
        return false;
      }

      // TODO: Validate BusinessName exists in OrganizationInformation
    }
    try {
      const cleaned = { ...formData };

      const resolvedOrganizationId = await resolveOrganizationIdForUser({
        uid: userId,
        profileType: cleaned.ProfileType || formData.ProfileType,
        cognitoOrgId: cognitoOrganizationId,
        emailCandidates: [cleaned.Email, cognitoEmail],
      });
      if (resolvedOrganizationId) {
        cleaned.organizationId = resolvedOrganizationId;
      }

      // Remove Email field from Dynamo update if not explicitly saving email
      if (!includeEmail && recordId) delete cleaned.Email;

      // Clean blank strings and trim
      Object.entries(cleaned).forEach(([k, v]) => {
        if (typeof v === "string") {
          cleaned[k] = v.trim();
          if (cleaned[k] === "") cleaned[k] = null;
        }
      });

      // GraphQL expects Int for these fields; coerce safely to avoid silent save failures.
      // Strip common formatting (e.g. EIN "12-3456789", ZIP+4 "12345-6789") before validating.
      const normalizeNullableInt = (value, fieldLabel) => {
        if (value === null || value === undefined || value === "") return null;
        const digitsOnly = String(value).trim().replace(/[\s-]/g, "");
        if (digitsOnly === "") return null;
        const parsed = Number(digitsOnly);
        if (!Number.isInteger(parsed)) {
          throw new Error(`${fieldLabel} must be a whole number.`);
        }
        return parsed;
      };

      cleaned.BusinessEIN = normalizeNullableInt(
        cleaned.BusinessEIN,
        "Business EIN",
      );
      cleaned.BusinessZipCode = normalizeNullableInt(
        cleaned.BusinessZipCode,
        "Business Zip Code",
      );

      // Always include userId
      cleaned.userId = userId;

      // Normalize Email to lowercase to avoid duplicates caused by case differences
      if (cleaned.Email && typeof cleaned.Email === "string") {
        cleaned.Email = cleaned.Email.trim().toLowerCase();
      }

      const ownerAliases = new Set(
        [userId, cleaned.Email, cognitoEmail, recordId]
          .map((value) => normalizeIdentity(value))
          .filter(Boolean),
      );

      // Clear out Professional-only fields if not selected
      const professionalFields = [
        "ProfessionalRole",
        "BusinessName",
        "BusinessEIN",
        "BusinessAddress",
        "BusinessCountry",
        "BusinessCity",
        "BusinessState",
        "BusinessZipCode",
        "BusinessPhoneNumber",
        "BusinessWebsite",
        "OrganizationAdministrator",
        "OrganizationEmployee",
      ];

      if (cleaned.ProfileType !== "Professional") {
        for (const field of professionalFields) {
          cleaned[field] = null; // Explicitly clear
        }
      }

      if (recordId) {
        try {
          await client.graphql({
            query: updateGeneralInformation,
            variables: { input: { id: recordId, ...cleaned } },
          });
          showNotification("Information updated.");
          return true;
        } catch (updateErr) {
          const updateMessage = String(
            updateErr?.errors?.[0]?.message || updateErr?.message || updateErr,
          ).toLowerCase();

          if (!updateMessage.includes("not authorized")) {
            throw updateErr;
          }

          const retryLookupEmail = cleaned.Email || formData.Email || "";
          const retryLookupFilter = retryLookupEmail
            ? {
                or: [
                  { userId: { eq: userId } },
                  { Email: { eq: retryLookupEmail } },
                ],
              }
            : { userId: { eq: userId } };

          const retryLookup = await client.graphql({
            query: listGeneralInformations,
            variables: {
              filter: retryLookupFilter,
              limit: 10,
            },
          });

          const retryItems =
            retryLookup?.data?.listGeneralInformations?.items || [];
          // Prefer an exact userId match (set by this app at creation/save time) over the
          // raw "owner" attribute, which can drift out of sync (sub format changes, legacy
          // records, etc.) and cause false "not authorized" rejections.
          const ownedRetryRecord =
            retryItems.find(
              (it) =>
                normalizeIdentity(it?.userId) === normalizeIdentity(userId),
            ) ||
            retryItems.find((it) =>
              ownerMatchesAliases(it?.owner, ownerAliases),
            );

          if (ownedRetryRecord?.id && ownedRetryRecord.id !== recordId) {
            setRecordId(ownedRetryRecord.id);
            await client.graphql({
              query: updateGeneralInformation,
              variables: {
                input: { id: ownedRetryRecord.id, ...cleaned },
              },
            });
            showNotification("Information updated.");
            return true;
          }

          throw updateErr;
        }
      } else {
        const res = await client.graphql({
          query: createGeneralInformation,
          variables: { input: cleaned },
        });
        const newId = res.data?.createGeneralInformation?.id;
        if (newId) setRecordId(newId);
        showNotification("Information saved.");
        return true;
      }
    } catch (err) {
      console.error(
        "Error saving data:",
        err,
        err?.errors || err?.message || "",
      );
      const friendlyError = getFriendlyGraphQLError(err);
      showNotification(`Error saving information: ${friendlyError}`);
      return false;
    }
  }

  async function updateCognitoEmail(newEmail) {
    try {
      await updateUserAttribute({
        userAttribute: { attributeKey: "email", value: newEmail },
      });
      setAwaitingVerification(true);
      showNotification("Verification code sent to your new email.");
    } catch (err) {
      console.error("Failed to update email:", err);
      showNotification("Failed to update email.");
    }
  }

  async function confirmEmailCode(code) {
    try {
      await confirmUserAttribute({
        userAttributeKey: "email",
        confirmationCode: code,
      });

      setAwaitingVerification(false);
      setConfirmCode("");
      showNotification("Email verified successfully! Please sign in again.");

      const saved = await saveDynamoFields(true);
      if (!saved) {
        showNotification("Email verified, but profile save failed.");
        return;
      }

      // Ensure connected UserLink records reflect the new email
      await syncUserLinkEmails(userId, formData.Email);

      setTimeout(async () => {
        await signOut();
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error("Verification failed:", err);
      showNotification("Incorrect code, please try again.");
    }
  }

  const handleSubmit = async () => {
    setOrgEmployeeError(""); // Clear previous errors

    if (formData.ProfileType === "Professional") {
      const requiredFields = [
        "ProfessionalRole",
        "BusinessName",
        "BusinessEIN",
        "BusinessAddress",
        "BusinessCountry",
        "BusinessCity",
        "BusinessState",
        "BusinessZipCode",
        "BusinessPhoneNumber",
        "BusinessWebsite",
      ];

      for (const field of requiredFields) {
        if (!formData[field] || formData[field].toString().trim() === "") {
          showNotification(`Please fill in: ${field}`);
          return;
        }
      }

      if (formData.OrganizationEmployee) {
        const isValid = await validateOrganizationEmployee();
        if (!isValid) {
          setOrgEmployeeError(
            "❌ You are not a registered employee for this organization.",
          );
          return;
        }
      }
    }

    if (awaitingVerification) {
      if (!confirmCode.trim()) {
        showNotification("Please enter the verification code.");
        return;
      }
      await confirmEmailCode(confirmCode.trim());
    } else {
      const saved = await saveDynamoFields();
      if (!saved) return;

      const cpSyncResult = await updateOrgCPIfEmployee();
      if (cpSyncResult?.reason === "auth-denied") {
        showNotification(
          "Profile saved, but employee directory sync is blocked by permissions. Ask your org admin to re-invite your email.",
        );
      } else if (cpSyncResult?.reason === "created") {
        showNotification(
          "Profile saved and employee directory record was created.",
        );
      } else if (cpSyncResult?.reason === "no-cp-match") {
        showNotification(
          "Profile saved, but no employee directory record could be matched for update.",
        );
      }

      // If ProfileType changed, update Cognito custom:accountType
      if (formData.ProfileType && formData.ProfileType !== cognitoAccountType) {
        try {
          await updateUserAttribute({
            userAttribute: {
              attributeKey: "custom:accountType",
              value: formData.ProfileType,
            },
          });
          setCognitoAccountType(formData.ProfileType);
          showNotification("Account type updated in Cognito.");
          // If the user switched to Professional, request backend to add them to the Professionals group
          if (formData.ProfileType === "Professional") {
            try {
              await fetch(
                "https://kpwdaqkfpck6zvyr67a3eatm6i0ywoup.lambda-url.us-east-1.on.aws/",
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    action: "addUserToGroups",
                    email: cognitoEmail || formData.Email,
                    groups: ["Professionals"],
                  }),
                },
              );
            } catch (err) {
              console.error("Failed to add user to Professionals group:", err);
            }
          }
        } catch (err) {
          console.error("Failed to update Cognito account type:", err);
          showNotification("Failed to update account type in Cognito.");
        }
      }

      if (formData.Email !== cognitoEmail) {
        await updateCognitoEmail(formData.Email);
      }
    }
  };
  return (
    <div>
      <div style={containerStyle}>
        <img
          src={(process.env.PUBLIC_URL || "") + "/Icon-GI-Blue.png"}
          alt="Logo"
          style={{
            width: "100%",
            maxWidth: 200,
            height: "auto",
            display: "block",
            margin: "10px auto 30px auto",
          }}
        />
        <h2 style={{ marginBottom: 24, color: "#0067B3" }}>
          General Information
        </h2>
        <p style={{ fontStyle: "italic", color: "gray", marginBottom: 12 }}>
          {statusMessage}
        </p>
        <p style={{ fontSize: 13, marginBottom: 20 }}>
          <strong>Your Cognito Email:</strong> {cognitoEmail}
        </p>

        {["FirstName", "LastName"].map((field) => (
          <div key={field}>
            <label style={labelStyle}>
              {field.replace(/([A-Z])/g, " $1")}:
            </label>
            <input
              type={
                field === "Height" || field === "Weight" ? "number" : "text"
              }
              name={field}
              value={formData[field] || ""}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
        ))}

        <div>
          <label style={labelStyle}>Email:</label>
          <input
            type="email"
            name="Email"
            value={formData.Email || ""}
            onChange={handleChange}
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Profile Type:</label>
          <select
            name="ProfileType"
            value={formData.ProfileType || ""}
            onChange={handleChange}
            style={inputStyle}
          >
            <option value="">Select type</option>
            <option value="Individual">Individual</option>
            <option value="Professional">Professional</option>
            <option value="Organization">Organization</option>
          </select>
        </div>
        {formData.ProfileType === "Professional" && (
          <>
            <div>
              <label style={labelStyle}>Organization Employee?</label>
              <label>
                <input
                  type="radio"
                  name="OrganizationEmployee"
                  checked={formData.OrganizationEmployee}
                  onChange={() => {
                    setFormData((prev) => ({
                      ...prev,
                      OrganizationEmployee: true,
                      Department: "",
                      ProgramsUnit: "",
                      Service: "",
                    }));
                    setOrgOptions({
                      Departments: [],
                      ProgramsUnits: [],
                      Services: [],
                    });
                  }}
                />
                Yes
              </label>

              <label>
                <input
                  type="radio"
                  name="OrganizationEmployee"
                  checked={!formData.OrganizationEmployee}
                  onChange={() => {
                    setFormData((prev) => ({
                      ...prev,
                      OrganizationEmployee: false,
                      Department: "",
                      ProgramsUnit: "",
                      Service: "",
                    }));
                    setOrgOptions({
                      Departments: [],
                      ProgramsUnits: [],
                      Services: [],
                    });
                  }}
                />
                No
              </label>
            </div>

            <div>
              <label style={labelStyle}>Business Name:</label>
              <input
                type="text"
                name="BusinessName"
                value={formData.BusinessName}
                onChange={handleChange}
                style={inputStyle}
              />
              {formData.OrganizationEmployee && (
                <p
                  style={{
                    fontSize: 13,
                    marginTop: 4,
                    color: BusinessVerified ? "green" : "red",
                  }}
                >
                  {BusinessStatus}
                </p>
              )}
            </div>

            <div>
              <label style={labelStyle}>Professional Role:</label>
              <select
                name="ProfessionalRole"
                value={formData.ProfessionalRole}
                onChange={handleChange}
                disabled={formData.OrganizationEmployee && !BusinessVerified}
                style={inputStyle}
              >
                <option value="">Select a role</option>
                <option value="Admin">Admin</option>
                <option value="Allergist">Allergist</option>
                <option value="Audiologist">Audiologist</option>
                <option value="Dentist">Dentist</option>
                <option value="Fitness Trainer">Fitness Trainer</option>
                <option value="Health/Lifestyle Coach">
                  Health/Lifestyle Coach
                </option>
                <option value="Leadership">Leadership</option>
                <option value="Mental Health Therapist">
                  Mental Health Therapist
                </option>
                <option value="Nurse">Nurse</option>
                <option value="Nurse Practitioner">Nurse Practitioner</option>
                <option value="Optometrist">Optometrist</option>
                <option value="Pastoral Care">Pastoral Care</option>
                <option value="Pediatrics">Pediatrics</option>
                <option value="Physician – MD">Physician – MD</option>
                <option value="Physician – Naturopathy">
                  Physician – Naturopathy
                </option>
                <option value="Physician – OD">Physician – OD</option>
                <option value="Social Worker">Social Worker</option>
              </select>
            </div>

            <div>
              <label style={labelStyle}>Business EIN:</label>
              <input
                type="text"
                name="BusinessEIN"
                disabled={formData.OrganizationEmployee}
                value={formData.BusinessEIN}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Business Country:</label>
              <input
                type="text"
                name="BusinessCountry"
                value={formData.BusinessCountry}
                disabled={formData.OrganizationEmployee}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Business Address:</label>
              <input
                type="text"
                name="BusinessAddress"
                disabled={formData.OrganizationEmployee}
                value={formData.BusinessAddress}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Business City:</label>
              <input
                type="text"
                name="BusinessCity"
                value={formData.BusinessCity}
                disabled={formData.OrganizationEmployee}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Business State:</label>
              <select
                type="text"
                name="BusinessState"
                value={formData.BusinessState}
                disabled={formData.OrganizationEmployee}
                onChange={handleChange}
                style={inputStyle}
              >
                <option value="">Select a State</option>
                {states.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Business Zip Code:</label>
              <input
                type="number"
                name="BusinessZipCode"
                value={formData.BusinessZipCode}
                disabled={formData.OrganizationEmployee}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Business Phone Number:</label>
              <input
                type="text"
                name="BusinessPhoneNumber"
                value={formData.BusinessPhoneNumber}
                disabled={formData.OrganizationEmployee}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Business Website:</label>
              <input
                type="text"
                name="BusinessWebsite"
                value={formData.BusinessWebsite}
                disabled={formData.OrganizationEmployee}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>
            {["Department", "ProgramsUnit", "Service"].map((fieldKey) => (
              <div key={fieldKey}>
                <label style={labelStyle}>
                  {fieldKey === "ProgramsUnit"
                    ? "Program/Unit"
                    : fieldKey.replace(/([A-Z])/g, " $1")}
                  :
                </label>
                {formData.OrganizationEmployee ? (
                  <select
                    name={fieldKey}
                    value={formData[fieldKey]}
                    disabled={
                      formData.OrganizationEmployee && !BusinessVerified
                    }
                    onChange={handleChange}
                    style={inputStyle}
                  >
                    <option value="">Select one</option>
                    {(fieldKey === "Department"
                      ? orgOptions.Departments
                      : fieldKey === "ProgramsUnit"
                        ? orgOptions.ProgramsUnits
                        : orgOptions.Services
                    ).map((opt, i) => (
                      <option key={i} value={opt}>
                        {opt}
                      </option>
                    ))}
                    <option value="None">None</option>
                    <option value="Not listed">Not listed</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    name={fieldKey}
                    value={formData[fieldKey]}
                    disabled={
                      formData.OrganizationEmployee && !BusinessVerified
                    }
                    onChange={handleChange}
                    style={inputStyle}
                  />
                )}
              </div>
            ))}

            {formData.OrganizationEmployee && (
              <div>
                <label style={labelStyle}>
                  <input
                    type="checkbox"
                    name="OrganizationAdministrator"
                    checked={formData.OrganizationAdministrator}
                    disabled={true}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        OrganizationAdministrator: e.target.checked,
                      }))
                    }
                  />{" "}
                  Organization Administrator
                </label>
              </div>
            )}
          </>
        )}

        {awaitingVerification && (
          <div style={{ marginTop: 30 }}>
            <hr style={{ marginBottom: 20, borderColor: "#ccc" }} />
            <label style={labelStyle}>Enter Verification Code:</label>
            <input
              type="text"
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              style={inputStyle}
            />
            <button
              type="button"
              onClick={() => updateCognitoEmail(formData.Email)}
              style={{
                ...buttonStyle,
                backgroundColor: "#333",
                marginTop: 8,
                marginBottom: 8,
              }}
            >
              Resend Code
            </button>
          </div>
        )}

        <button type="button" onClick={handleSubmit} style={buttonStyle}>
          {awaitingVerification
            ? "Confirm Email Change"
            : recordId
              ? "Save Changes"
              : "Save Information"}
        </button>
        {orgEmployeeError && (
          <div style={{ marginTop: 12, color: "red", fontWeight: 600 }}>
            {orgEmployeeError}
          </div>
        )}

        {notification && <div style={notificationStyle}>{notification}</div>}
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            onClick={async () => {
              try {
                const confirmText = window.prompt(
                  "Type DELETE to permanently delete your account:",
                );
                if (confirmText !== "DELETE") return;
                setIsDeleting(true);
                setStatusMessage("Deleting account...");

                // Attempt to delete Cognito user
                try {
                  await deleteUser();
                  console.log("Cognito user deleted");
                } catch (err) {
                  console.error("Failed to delete Cognito user:", err);
                }

                // Sign out (redirects to sign-in)
                try {
                  await signOut();
                } catch (err) {
                  console.error("Sign out after delete failed:", err);
                  try {
                    window.location.href = "/";
                  } catch (e) {
                    /* ignore */
                  }
                }
              } finally {
                setIsDeleting(false);
              }
            }}
            disabled={isDeleting}
            style={{
              marginTop: 12,
              width: "100%",
              backgroundColor: isDeleting ? "#ffaaaa" : "#cc0000",
              color: "white",
              fontWeight: "700",
              fontSize: 16,
              borderRadius: 8,
              padding: "12px 0",
              border: "none",
              cursor: isDeleting ? "not-allowed" : "pointer",
            }}
          >
            {isDeleting ? "Deleting account..." : "Delete Account"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Styles
const containerStyle = {
  width: 350,
  minWidth: 300,
  margin: "0 auto",
  alignItems: "center",
  backgroundColor: "white",
  padding: 24,
  borderRadius: 12,
  boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  color: "#333",
};

const labelStyle = {
  display: "block",
  fontWeight: "600",
  marginBottom: 6,
  marginTop: 16,
  fontSize: 14,
  textAlign: "left",
};

const inputStyle = {
  width: "100%",
  padding: 10,
  fontSize: 15,
  borderRadius: 8,
  border: "1.5px solid #ccc",
  boxSizing: "border-box",
};

const buttonStyle = {
  marginTop: 16,
  width: "100%",
  backgroundColor: "#0067B3",
  color: "white",
  fontWeight: "700",
  fontSize: 16,
  borderRadius: 8,
  padding: "12px 0",
  border: "none",
  cursor: "pointer",
  userSelect: "none",
  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
};

const notificationStyle = {
  marginTop: 16,
  padding: 12,
  borderRadius: 6,
  backgroundColor: "#e0f0ff",
  color: "#0067B3",
  fontWeight: "600",
  textAlign: "center",
};
